import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  Alert,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteLogoIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../store';
import { profileService, agencyService } from '../services/api';
import type { Client, User, Agency } from '../types';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useAppSelector((state) => state.auth.user);

  const [profile, setProfile] = useState<Client | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    civilite: '',
    date_naissance: '',
    lieu_naissance: '',
    email: '',
    phone: '',
    address: '',
    accept_sms_email: false,
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const logoBlobRef = useRef<string | null>(null);

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role_name: 'client' as 'client' | 'field_agent' | 'agent_terrain' | 'agent_technique' | 'agent_comptoir',
    agency_id: '' as number | '',
  });
  const [editUserForm, setEditUserForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role_name: 'client' as 'client' | 'agent_terrain' | 'agent_technique' | 'agent_comptoir',
    agency_id: '' as number | '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const isAdmin = currentUser?.role?.name === 'admin';
  const hasClient = Boolean(currentUser?.client_id);

  useEffect(() => {
    agencyService.getAll().then((res) => setAgencies(res.data.data || [])).catch(() => setAgencies([]));
  }, []);

  useEffect(() => {
    if (isAdmin || !hasClient) {
      setLoading(false);
      return;
    }
    loadProfile();
  }, [isAdmin, hasClient]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const [profileRes, usersRes] = await Promise.all([
        profileService.getProfile(),
        profileService.getUsers(),
      ]);
      setProfile(profileRes.data.data);
      setUsers(usersRes.data.data);
      setProfileForm({
        name: profileRes.data.data.name,
        civilite: profileRes.data.data.civilite ?? '',
        date_naissance: profileRes.data.data.date_naissance ?? '',
        lieu_naissance: profileRes.data.data.lieu_naissance ?? '',
        email: profileRes.data.data.email,
        phone: profileRes.data.data.phone || '',
        address: profileRes.data.data.address || '',
        accept_sms_email: profileRes.data.data.accept_sms_email ?? false,
      });
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Impossible de charger le profil';
      setError(msg);
      if (err.response?.status === 403) {
        setProfile(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await profileService.getUsers();
      setUsers(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Impossible de charger les utilisateurs');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      setError(null);
      await profileService.updateProfile(profileForm);
      await loadProfile();
      if (logoFile) {
        setUploadingLogo(true);
        await profileService.uploadLogo(logoFile);
        setLogoFile(null);
        setUploadingLogo(false);
        await loadProfile();
      }
      setEditingProfile(false);
      setSuccessMessage('Profil mis à jour avec succès.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Échec de la mise à jour du profil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('Format accepté : JPEG, PNG, GIF ou WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Taille maximale : 5 Mo.');
      return;
    }
    setError(null);
    // Show local preview immediately before the upload completes
    if (logoBlobRef.current) URL.revokeObjectURL(logoBlobRef.current);
    const blobUrl = URL.createObjectURL(file);
    logoBlobRef.current = blobUrl;
    setLogoPreviewUrl(blobUrl);
    try {
      setUploadingLogo(true);
      await profileService.uploadLogo(file);
      await loadProfile();
      setSuccessMessage('Logo mis à jour.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Échec du téléchargement du logo');
    } finally {
      setUploadingLogo(false);
      // Clean up blob URL after server URL is loaded
      if (logoBlobRef.current) {
        URL.revokeObjectURL(logoBlobRef.current);
        logoBlobRef.current = null;
      }
      setLogoPreviewUrl(null);
    }
  };

  const handleDeleteLogo = async () => {
    if (!profile?.logo_path || !window.confirm('Supprimer le logo ?')) return;
    try {
      setUploadingLogo(true);
      await profileService.deleteLogo();
      await loadProfile();
      setSuccessMessage('Logo supprimé.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Échec de la suppression du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setSavingUser(true);
      setError(null);
      await profileService.createUser(userForm);
      setUserDialogOpen(false);
      setUserForm({ name: '', email: '', password: '', phone: '', role_name: 'client', agency_id: '' });
      await loadUsers();
      await loadProfile();
      setSuccessMessage('Utilisateur créé.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Échec de la création de l\'utilisateur');
    } finally {
      setSavingUser(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      setSavingUser(true);
      setError(null);
      const data: any = {
        name: editUserForm.name,
        email: editUserForm.email,
        phone: editUserForm.phone,
      };
      // Role is required for agents/clients and used to compute `department` server-side.
      if (editUserForm.role_name) data.role_name = editUserForm.role_name;
      // Allow clearing agency.
      data.agency_id = editUserForm.agency_id === '' ? null : Number(editUserForm.agency_id);
      if (editUserForm.password.trim()) data.password = editUserForm.password;
      await profileService.updateUser(selectedUser.id, data);
      setEditUserDialogOpen(false);
      setSelectedUser(null);
      await loadUsers();
      setSuccessMessage('Utilisateur mis à jour.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Échec de la mise à jour');
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (user.id === currentUser?.id) {
      setError('Vous ne pouvez pas supprimer votre propre compte.');
      return;
    }
    if (!window.confirm(`Supprimer l'utilisateur ${user.name} ?`)) return;
    try {
      await profileService.deleteUser(user.id);
      await loadUsers();
      await loadProfile();
      setSuccessMessage('Utilisateur supprimé.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Échec de la suppression');
    }
  };

  const openEditUser = (user: User) => {
    setSelectedUser(user);
    const normalizedRoleName =
      user.role?.name === 'field_agent' ? 'agent_terrain' : (user.role?.name || 'client');
    setEditUserForm({
      name: user.name,
      email: user.email,
      password: '',
      phone: user.phone || '',
      role_name: normalizedRoleName as any,
      agency_id: (user.agency_id as any) ?? '',
    });
    setEditUserDialogOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (isAdmin || !hasClient) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Le profil entreprise est réservé aux comptes client. En tant qu'administrateur, vous gérez les clients depuis le tableau de bord.
        </Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/dashboard')}>
          Retour au tableau de bord
        </Button>
      </Box>
    );
  }

  if (error && !profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/dashboard')}>
          Retour au tableau de bord
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Company profile card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<BusinessIcon color="primary" sx={{ fontSize: 32 }} />}
          title="Profil de l'entreprise"
          subheader={profile ? `${profile.users_count ?? users.length} utilisateur(s) · Limite : ${profile.max_users}` : undefined}
          action={
            !editingProfile ? (
              <Button startIcon={<EditIcon />} onClick={() => setEditingProfile(true)}>
                Modifier
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" startIcon={<CloseIcon />} onClick={() => { setEditingProfile(false); setLogoFile(null); loadProfile(); }}>
                  Annuler
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveProfile} disabled={savingProfile}>
                  {savingProfile ? <CircularProgress size={20} /> : 'Enregistrer'}
                </Button>
              </Box>
            )
          }
        />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flexShrink: 0 }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={logoPreviewUrl ?? profile?.logo_url ?? undefined}
                  variant="rounded"
                  sx={{ width: 120, height: 120, bgcolor: 'grey.200' }}
                >
                  <BusinessIcon sx={{ fontSize: 48 }} />
                </Avatar>
                {uploadingLogo && (
                  <Box sx={{ position: 'absolute', top: 0, left: 0, width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.35)', borderRadius: 1 }}>
                    <CircularProgress size={28} sx={{ color: 'white' }} />
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" component="label" startIcon={<UploadIcon />} disabled={uploadingLogo}>
                  {uploadingLogo ? <CircularProgress size={16} /> : 'Changer le logo'}
                  <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                </Button>
                {profile?.logo_path && (
                  <Button size="small" color="error" startIcon={<DeleteLogoIcon />} onClick={handleDeleteLogo} disabled={uploadingLogo}>
                    Supprimer
                  </Button>
                )}
              </Box>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Nom"
                value={profileForm.name}
                onChange={(e) => setProfileForm((p) => ({ ...p, name: e.target.value }))}
                disabled={!editingProfile}
                fullWidth
              />
              <FormControl fullWidth size="small" disabled={!editingProfile}>
                <InputLabel>Civilité</InputLabel>
                <Select
                  label="Civilité"
                  value={profileForm.civilite}
                  onChange={(e) => setProfileForm((p) => ({ ...p, civilite: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="Mme">Mme</MenuItem>
                  <MenuItem value="Mlle">Mlle</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Date de naissance"
                type="date"
                value={profileForm.date_naissance}
                onChange={(e) => setProfileForm((p) => ({ ...p, date_naissance: e.target.value }))}
                disabled={!editingProfile}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Lieu de naissance"
                value={profileForm.lieu_naissance}
                onChange={(e) => setProfileForm((p) => ({ ...p, lieu_naissance: e.target.value }))}
                disabled={!editingProfile}
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm((p) => ({ ...p, email: e.target.value }))}
                disabled={!editingProfile}
                fullWidth
              />
              <TextField
                label="Téléphone"
                value={profileForm.phone}
                onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))}
                disabled={!editingProfile}
                fullWidth
              />
              <TextField
                label="Adresse"
                multiline
                rows={2}
                value={profileForm.address}
                onChange={(e) => setProfileForm((p) => ({ ...p, address: e.target.value }))}
                disabled={!editingProfile}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={profileForm.accept_sms_email}
                    onChange={(e) => setProfileForm((p) => ({ ...p, accept_sms_email: e.target.checked }))}
                    disabled={!editingProfile}
                  />
                }
                label="Accepte SMS / email"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Users card */}
      <Card>
        <CardHeader
          avatar={<PeopleIcon color="primary" sx={{ fontSize: 32 }} />}
          title="Utilisateurs"
          subheader="Gérer les comptes utilisateurs de votre entreprise"
          action={
            users.length < (profile?.max_users ?? 0) ? (
              <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setUserDialogOpen(true)}>
                Ajouter un utilisateur
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nombre maximum d'utilisateurs atteint ({profile?.max_users})
              </Typography>
            )
          }
        />
        <CardContent>
          {loadingUsers ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nom</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Téléphone</TableCell>
                    <TableCell>Rôle</TableCell>
                    <TableCell>Agence</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        Aucun utilisateur.
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                              {u.name?.[0]?.toUpperCase()}
                            </Avatar>
                            {u.name}
                            {u.id === currentUser?.id && (
                              <Typography component="span" variant="caption" color="primary">(vous)</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.phone || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {{ client: 'Client', field_agent: 'Agent de terrain', agent_terrain: 'Agent de terrain', agent_technique: 'Agent technique', agent_comptoir: 'Agent de comptoir' }[u.role?.name || ''] || u.role?.name || 'Client'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {agencies.find((a) => a.id === (u as any).agency_id)?.name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditUser(u)} title="Modifier">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteUser(u)}
                            disabled={u.id === currentUser?.id}
                            title={u.id === currentUser?.id ? 'Vous ne pouvez pas vous supprimer' : 'Supprimer'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create user dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un utilisateur</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nom"
              value={userForm.name}
              onChange={(e) => setUserForm((p) => ({ ...p, name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={userForm.email}
              onChange={(e) => setUserForm((p) => ({ ...p, email: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              value={userForm.password}
              onChange={(e) => setUserForm((p) => ({ ...p, password: e.target.value }))}
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Téléphone"
              value={userForm.phone}
              onChange={(e) => setUserForm((p) => ({ ...p, phone: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Rôle</InputLabel>
              <Select
                value={userForm.role_name}
                label="Rôle"
                onChange={(e) => setUserForm((p) => ({ ...p, role_name: e.target.value as typeof userForm.role_name }))}
              >
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="agent_terrain">Agent de terrain</MenuItem>
                <MenuItem value="agent_technique">Agent technique</MenuItem>
                <MenuItem value="agent_comptoir">Agent de comptoir</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Agence</InputLabel>
              <Select
                value={userForm.agency_id}
                label="Agence"
                onChange={(e) => setUserForm((p) => ({ ...p, agency_id: e.target.value as number | '' }))}
              >
                <MenuItem value="">— Aucune —</MenuItem>
                {agencies.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={!userForm.name || !userForm.email || !userForm.password || savingUser}
          >
            {savingUser ? <CircularProgress size={20} /> : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={editUserDialogOpen} onClose={() => { setEditUserDialogOpen(false); setSelectedUser(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier l'utilisateur</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nom"
              value={editUserForm.name}
              onChange={(e) => setEditUserForm((p) => ({ ...p, name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={editUserForm.email}
              onChange={(e) => setEditUserForm((p) => ({ ...p, email: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Nouveau mot de passe (laisser vide pour ne pas changer)"
              type={showPassword ? 'text' : 'password'}
              value={editUserForm.password}
              onChange={(e) => setEditUserForm((p) => ({ ...p, password: e.target.value }))}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Téléphone"
              value={editUserForm.phone}
              onChange={(e) => setEditUserForm((p) => ({ ...p, phone: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth size="small">
              <InputLabel>Rôle</InputLabel>
              <Select
                value={editUserForm.role_name}
                label="Rôle"
                onChange={(e) => setEditUserForm((p) => ({ ...p, role_name: e.target.value as any }))}
              >
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="agent_terrain">Agent de terrain</MenuItem>
                <MenuItem value="agent_technique">Agent technique</MenuItem>
                <MenuItem value="agent_comptoir">Agent de comptoir</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Agence</InputLabel>
              <Select
                value={editUserForm.agency_id}
                label="Agence"
                onChange={(e) => setEditUserForm((p) => ({ ...p, agency_id: e.target.value as any }))}
              >
                <MenuItem value="">— Aucune —</MenuItem>
                {agencies.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditUserDialogOpen(false); setSelectedUser(null); }}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateUser} disabled={!editUserForm.name || !editUserForm.email || savingUser}>
            {savingUser ? <CircularProgress size={20} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
